/**
 * TextInput, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/TextInputComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { labelLengthWarnings } from '../labelLength';
import { reservedFieldNameErrors } from '../fieldName';

export const TextInputCore = {
  type: 'TextInput',
  name: 'Text Input',
  icon: '📝',
  description: 'Single-line text field for short answers.',
  category: 'input',
  maxPerScreen: null,
  getDefaultProperties() {
    return {
      name: 'text_input',
      label: 'Enter text',
      'input-type': 'text',
      required: false,
      'helper-text': '',
      'min-chars': '',
      'max-chars': '',
      'init-value': '',
      'error-message': '',
      enabled: true,
      visible: true,
      pattern: '',
    };
  },
  getPropertySchema() {
    return {
      name: {
        type: 'string',
        label: 'Field Name',
        required: true,
      },
      label: {
        type: 'string',
        label: 'Label',
        required: true,
      },
      'input-type': {
        type: 'select',
        label: 'Input Type',
        required: true,
        options: ['text', 'number', 'email', 'password', 'phone'],
      },
      required: {
        type: 'boolean',
        label: 'Required',
        required: false,
      },
      'helper-text': {
        type: 'string',
        label: 'Helper Text',
        required: false,
      },
      'min-chars': {
        type: 'string',
        label: 'Min Characters',
        required: false,
      },
      'max-chars': {
        type: 'string',
        label: 'Max Characters',
        required: false,
      },
      'init-value': {
        type: 'string',
        label: 'Initial Value',
        required: false,
      },
      'error-message': {
        type: 'string',
        label: 'Error Message',
        required: false,
      },
      enabled: {
        type: 'boolean',
        label: 'Enabled',
        required: false,
      },
      visible: {
        type: 'boolean',
        label: 'Visible',
        required: false,
      },
      pattern: {
        type: 'string',
        label: 'Pattern (Regex)',
        required: false,
      },
    };
  },
  validateProperties(properties) {
    const errors = [];
    const warnings = [];

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }

    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore and contain only letters, numbers, and underscores');
    }

    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Label is required');
    }

    warnings.push(...labelLengthWarnings('TextInput', properties.label));

    if (properties.label && properties.label.length > 80) {
      errors.push('Label must be 80 characters or less');
    }

    if (properties['min-chars'] && properties['max-chars']) {
      var minChars = parseInt(properties['min-chars'], 10);
      var maxChars = parseInt(properties['max-chars'], 10);
      if (!isNaN(minChars) && !isNaN(maxChars) && minChars > maxChars) {
        errors.push('Min characters cannot be greater than max characters');
      }
    }

    if (properties['max-chars']) {
      var maxCharsVal = parseInt(properties['max-chars'], 10);
      if (!isNaN(maxCharsVal) && maxCharsVal > 80) {
        warnings.push('Max characters should typically be 80 or less for text inputs');
      }
    }

    if (properties.pattern) {
      try {
        new RegExp(properties.pattern);
      } catch (e) {
        errors.push('Invalid regex pattern');
      }
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    var result = {
      type: 'TextInput',
      name: properties.name,
      label: properties.label,
      'input-type': properties['input-type'] || 'text',
      required: properties.required || false,
    };

    if (properties['helper-text']) {
      result['helper-text'] = properties['helper-text'];
    }

    if (properties['min-chars']) {
      result['min-chars'] = properties['min-chars'];
    }

    if (properties['max-chars']) {
      result['max-chars'] = properties['max-chars'];
    }

    if (properties['init-value']) {
      result['init-value'] = properties['init-value'];
    }

    if (properties['error-message']) {
      result['error-message'] = properties['error-message'];
    }

    if (properties.enabled === false) {
      result.enabled = false;
    }

    if (properties.visible === false) {
      result.visible = false;
    }

    if (properties.pattern) {
      result.pattern = properties.pattern;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    var helperText = whatsappComponent['helper-text'];
    if (typeof helperText === 'object' && helperText !== null) {
      helperText = helperText.text || '';
    }

    var errorMessage = whatsappComponent['error-message'];
    if (typeof errorMessage === 'object' && errorMessage !== null) {
      errorMessage = errorMessage.text || '';
    }

    return {
      name: whatsappComponent.name,
      label: whatsappComponent.label,
      'input-type': whatsappComponent['input-type'] || 'text',
      required: whatsappComponent.required || false,
      'helper-text': helperText || '',
      'min-chars': whatsappComponent['min-chars'] || '',
      'max-chars': whatsappComponent['max-chars'] || '',
      'init-value': whatsappComponent['init-value'] || '',
      'error-message': errorMessage || '',
      enabled: whatsappComponent.enabled !== false,
      visible: whatsappComponent.visible !== false,
      pattern: whatsappComponent.pattern || '',
    };
  },
};
